<template>
  <NuxtLayout>
    <AttendanceGuestRegistration
      v-if="isGuestFlow"
      :allTicketSelectionData="sortedGuestTickets"
      :displayTicketName="displayTicketName"
      :loadingTicketData="loadingTicketData"
      :ticketMap="ticketMap"
      :addonSummary="addonSummary"
      :postOrderDetails="postOrderDetails"
      :eventData="eventData"
      :route="route"
      :allCustomQuestions="allCustomQuestions"
      :allMealChoices="allMealChoices"
    />
    <AttendanceTableRegistration
      v-else-if="isTableFlow"
      :allTicketSelectionData="sortedTableTickets"
      :displayTicketName="displayTicketName"
      :loadingTicketData="loadingTicketData"
      :ticketMap="ticketMap"
      :addonSummary="addonSummary"
      :postOrderDetails="postOrderDetails"
      :eventData="eventData"
      :route="route"
      :allCustomQuestions="allCustomQuestions"
      :allMealChoices="allMealChoices"
    />
  </NuxtLayout>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeMount } from 'vue';
import { useRoute } from 'vue-router';
import BaseUrl from '@/api/BaseUrl';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import useEventDataStore from '@/stores/eventDataStore';
import { useEventRegistrationApi } from '@/composables/attendance/useEventRegistrationApi';
import AttendanceGuestRegistration from '@/components/attendance/AttendanceGuestRegistration.vue';
import AttendanceTableRegistration from '@/components/attendance/AttendanceTableRegistration.vue';
import ENDPOINTS from '@/api/endpoints';
import { Currency } from '~/utils/currency';

const route = useRoute();
const router = useRouter();
const { eventData } = storeToRefs(useEventDataStore());

const isPaidEvent = computed(() => eventData.value?.is_paid_event);

const { allTicketSelectionData, loadingTicketData, ticketMap, addonSummary, postOrderDetails } =
  useEventRegistrationApi(route, isPaidEvent.value);

const FILTERS = {
  TICKETS: 'tickets',
  TABLES: 'tables',
};

const selectedFilter = ref(route.query.selectedFilter || FILTERS.TICKETS);

const isGuestFlow = computed(() => selectedFilter.value === FILTERS.TICKETS);
const isTableFlow = computed(() => selectedFilter.value === FILTERS.TABLES);

function getTicketType(ticket, eventData) {
  const now = DateTime.now();
  let isDateValid = true;
  if (ticket.start_date || ticket.end_date) {
    const startDate = ticket.start_date
      ? DateTime.fromISO(ticket.start_date, { zone: eventData?.event_timezone })
      : null;
    const endDate = ticket.end_date
      ? DateTime.fromISO(ticket.end_date, { zone: eventData?.event_timezone })
      : null;
    if (startDate && endDate) {
      isDateValid = now >= startDate && now <= endDate;
    } else if (startDate) {
      isDateValid = now >= startDate;
    } else if (endDate) {
      isDateValid = now <= endDate;
    }
  }

  if (ticket.is_paused || !isDateValid) return 'unavailable';
  // FUN-43: `sold_out` is the server's answer; the rest are older fields kept for
  // surfaces that still send them.
  if (
    ticket.sold_out ||
    ticket.available_qty === 0 ||
    ticket.status === 'sold_out' ||
    ticket.status === 'unavailable'
  )
    return 'unavailable';
  if (ticket.is_early_bird && ticket.is_early_bird_active) return 'early_bird';
  if (ticket.price_type === 'free' || ticket.config?.price === 0) return 'free';
  if (ticket.price_type === 'paid') return 'paid';
  if (ticket.price_type === 'donation') return 'donation';
  if (ticket.price_type === 'gradual_discounts') return 'gradual_discount';
  return 'invalid';
}

const getAllCustomQuestionsUrl = computed(
  () =>
    `${BaseUrl.get(ENDPOINTS.EVENTS.GET_CUSTOM_QUESTIONS)}${ENDPOINTS.EVENTS.GET_CUSTOM_QUESTIONS}?event_id=${
      route.params.id
    }`,
);

const { data: allCustomQuestions, execute: fetchAllCustomQuestions } = useFetch(
  getAllCustomQuestionsUrl,
  {
    credentials: 'include',
    headers: Headers,
  },
);

const getAllMealChoicesUrl = computed(
  () =>
    `${BaseUrl.get(ENDPOINTS.EVENTS.GET_ADD_ONS)}${ENDPOINTS.EVENTS.GET_ADD_ONS}?event_id=${route.params.id}`,
);

const { data: allMealChoices, execute: fetchAllMealChoices } = useFetch(getAllMealChoicesUrl, {
  credentials: 'include',
  headers: Headers,
});

const ticketTypeOrder = {
  free: 0,
  early_bird: 1,
  paid: 2,
  donation: 3,
  gradual_discount: 4,
  unavailable: 5,
  invalid: 6,
};

const sortTickets = (a, b) => {
  const typeA = getTicketType(a, eventData.value);
  const typeB = getTicketType(b, eventData.value);
  return ticketTypeOrder[typeA] - ticketTypeOrder[typeB];
};

const displayTicketName = (ticket) => {
  if (!ticket) return '';
  let results = ticket?.name;
  if (ticket?.config?.price === 0 || ticket?.price_type === 'free') {
    return results;
  }
  if (ticket?.price_type === 'paid') {
    results += ` - ${Currency.formatCents(ticket.config?.price)}`;
    return results;
  }
  // if (ticket?.price_type === "donation") {
  //   results += ` - Donations`;
  //   return results;
  // }
  // if (ticket?.price_type === "gradual_discounts") {
  //   results += ` - Gradual Discounts! Starting at $${ticket.config?.price?.discount_pattern.split(",")[0]}`;
  //   return results;
  // }
  results += ` - ${Currency.formatCents(ticket.config?.price)}`;
  return results;
};

const sortedGuestTickets = computed(() => {
  const tickets = allTicketSelectionData.value?.filter((el) => !el.table_id) ?? [];
  return tickets.slice().sort(sortTickets);
});

const sortedTableTickets = computed(() => {
  const tickets = allTicketSelectionData.value?.filter((el) => !!el.table_id) ?? [];
  return tickets.slice().sort(sortTickets);
});

onBeforeMount(() => {
  if (!eventData.value) {
    return;
  }

  // FUN-40: an archived event has no registration window left to reopen, so a
  // guest following an invitation sent before the archive goes to the event's
  // page rather than the closed-registration interstitial -- that page exists
  // to explain a closed window, and its own primary action is "Return To Event
  // Page". Anonymous public arrivals keep the interstitial.
  if (eventData.value.is_archived && route.query?.invite_code) {
    return router.replace({
      name: 'attendance-id-event',
      params: { id: route.params.id },
    });
  }

  if (eventData.value.registration_open === false) {
    return router.replace({
      name: 'attendance-id-registration-closed',
      params: { id: route.params.id },
    });
  }
});

onMounted(() => {
  fetchAllCustomQuestions();
  fetchAllMealChoices();
});
</script>
